/**
 * OutageTable Component - Displays outage device data in a paginated table
 * 
 * Features:
 * - Paginated outage device data display
 * - Device status chips with color coding
 * - Clickable offline events to open detailed drawer
 * - Responsive table layout with device outage information
 * - Integration with OfflineEventsDrawer for detailed event analysis
 * - No data state handling
 * 
 * @author CTS - Ford PM UI Team
 
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import OfflineEventsDrawer from './OfflineEventsDrawer.jsx';
import StatusIndicator from '../../components/StatusIndicator.jsx';
import { AppLabels } from '../../constant/constant';
import { exportDevicesReport } from '../../api/outageApi';
import DateUtils from '../../utils/DateUtils.jsx';
import { FORMAT_TYPES, formatOutageDuration } from '../../utils/durationUtils.jsx';

import {
  OutagePanel,
  OutageTableContainer,
  OutageNoResults,
  OutagePanelTitle,
  OutagePaginationContainer,
  OutageTablePagination,
  OutageLinkButton
} from './Outage.styles';
import { DashboardMonospaceText } from '../dashBoard/Dashboard.styles';
import { renderCriticalDeviceListWithDualSupply, formattedText } from '../../utils/TextTooltipUtils';


// Constants to avoid magic numbers
const PAGINATION_CONFIG = {
  DEFAULT_ROWS_PER_PAGE: 10,
  ROWS_PER_PAGE_OPTIONS: [5, 10, 25, 50, 100],
  INITIAL_PAGE: 0
};


/**
 * Table column configuration object
 * Defines both header labels and how to render body values
 */
const TABLE_COLUMNS = [
   {
    key: 'deviceName',
    header: 'Device',
    render: (row) => (
      <Typography sx={{ fontWeight: 700, color: 'rgb(19, 34, 52)', fontSize:'12px' }}>
        {row.deviceName}
      </Typography>
    )
  },
  {
    key: 'deviceMac',
    header: 'Mac ID',
    render: (row) => <DashboardMonospaceText>{row.deviceMac}</DashboardMonospaceText>
  },
  {
    key: 'location',
    header: 'Location',
    render: (row) => row.location
  },
  // {
  //   key: 'deviceName',
  //   header: 'Device Name',
  //   render: (row) => row.deviceName
  // },
  {
    key: 'smartPlugLocation',
    header: 'Plug',
    render: (row) => formattedText(row.smartPlugLocation)
  },
  {
    key: 'modemId',
    header: 'Modem ID',
    render: (row) => <DashboardMonospaceText>{row.modemId}</DashboardMonospaceText>
  },
  {
    key: 'status',
    header: 'Status',
    render: (row) => (
      <StatusIndicator status={row.statusNow} size="medium" />
    )
  },
  {
     key: 'criticalDeviceList',
    header: 'Critical Devices',
    render: (row) => renderCriticalDeviceListWithDualSupply(
      row.criticalDeviceList, 
      row.criticalDevices, 
      row.dualSupplyDevices
    ),
  },
  {
    key: 'outageDisplay',
    // header: (
    //   <>
    //     Outage<br/>
    //     (days hours min sec)
    //   </>
    // ),
    header: 'Outage',
     render: (row) => (
      <Typography sx={{ textAlign: 'right', fontSize:'12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
        {row.outageDisplay}
      </Typography>
    )
  },
  {
    key: 'offlineEvents',
    header: 'Events',
    render: (row, handleOfflineEventsClick) => 
      row.offlineEvents > 0 ? (
        <OutageLinkButton onClick={() => handleOfflineEventsClick(row)}>
          {row.offlineEvents}
        </OutageLinkButton>
      ) : row.offlineEvents
  }
];

/**
 * OutageTable Component - Displays outage device data with pagination
 * 
 * @param {Array} rows - Outage device data to display
 * @param {boolean} loading - Loading state for initial data
 * @param {string} error - Error message if API fails
 * @param {boolean} devicesLoading - Loading state for device search
 * @param {string} startDate - Start date for offline events filtering
 * @param {string} endDate - End date for offline events filtering
 * @param {string} contextText - Search context text to display in title
 */
const OutageTable = ({ rows, loading, error, devicesLoading, startDate, endDate, contextText , isDownloadButtonEnabled, searchParams}) => {
  // Pagination state
  const [page, setPage] = useState(PAGINATION_CONFIG.INITIAL_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_CONFIG.DEFAULT_ROWS_PER_PAGE);

  // Offline events drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Error toast state
  const [errorToast, setErrorToast] = useState({ open: false, message: '' });
  // Calculate paginated data
  const paginatedRows = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return rows.slice(startIndex, endIndex);
  }, [rows, page, rowsPerPage]);

  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(PAGINATION_CONFIG.INITIAL_PAGE); // Reset to first page when changing rows per page
  };

  // Function to handle offline events click
  const handleOfflineEventsClick = (device) => {
    setSelectedDevice(device);
    setDrawerOpen(true);
  };

  // Function to close drawer
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedDevice(null);
  };

  // Function to close error toast
  const handleErrorToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setErrorToast({ ...errorToast, open: false });
  };

  // Function to handle export button click
  const handleExportClick = async () => {
    if (!searchParams) {
      console.error('Search parameters not provided for export');
      return;
    }

    try {
      setIsExporting(true);
      const exportData = await exportDevicesReport(searchParams);
      console.log('exportData', exportData);
      
      // Create Excel content from the export data
      const excelData = convertToExcel(exportData.devices || []);
      
      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Devices Report');
      
      // Generate filename with date range and current local date and time
      const now = new Date();
      const todayDateTime = now.getFullYear() + '-' + 
        String(now.getMonth() + 1).padStart(2, '0') + '-' + 
        String(now.getDate()).padStart(2, '0') + ' ' +
        String(now.getHours()).padStart(2, '0') + ':' + 
        String(now.getMinutes()).padStart(2, '0') + ':' + 
        String(now.getSeconds()).padStart(2, '0');
      const filename = `offline_devices_report_from_${startDate}_to_${endDate}_and_downloaded_at_${todayDateTime}.xlsx`;
      
      // Download the Excel file
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      console.error('Error exporting devices report:', error);
      setErrorToast({ 
        open: true, 
        message: 'Unable to export devices report. Please try again.' 
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Function to count critical devices including comma-separated values
  const countCriticalDevices = (device) => {
    let totalCount = 0;
    
    // Count critical devices
    const criticalDevices = device.criticaldevices || [];
    criticalDevices.forEach(cd => {
      if (cd.critical_id && cd.critical_id.trim() !== '' && cd.critical_id !== 'NA') {
        // Count comma-separated items
        const items = cd.critical_id.split(',').filter(item => item.trim() !== '');
        totalCount += items.length;
      }
    });
    
    // Count dual critical devices
    const dualCriticalDevices = device.dualcriticaldevices || [];
    dualCriticalDevices.forEach(dcd => {
      if (dcd.dual_critical_id && dcd.dual_critical_id.trim() !== '' && dcd.dual_critical_id !== 'NA') {
        // Count comma-separated items
        const items = dcd.dual_critical_id.split(',').filter(item => item.trim() !== '');
        totalCount += items.length;
      }
    });
    
    return totalCount;
  };

  // Function to convert export data to Excel format
  const convertToExcel = (devices) => {
    if (!devices || devices.length === 0) {
      return [{ 'Message': 'No data available for export' }];
    }

    const exportData = [];
    
    devices.forEach((device, deviceIndex) => {
      const outageEvents = device.outage_Events || [];
      const criticalDevicesCount = countCriticalDevices(device);
      
      if (outageEvents.length > 0) {
        // Add each outage event as a separate row
        outageEvents.forEach((event, eventIndex) => {
          exportData.push({
            'S.No': deviceIndex + 1,
            'Site Name': device.store_name || AppLabels.notApplicable,
            'Location Name': device.location_name || AppLabels.notApplicable,
            'Device Name': device.sp_DeviceName || AppLabels.notApplicable,
            'Critical Devices Count': criticalDevicesCount,
            'Offline Time': event.offline_time ? DateUtils.formatDateWithLocalZone(event.offline_time) : AppLabels.notApplicable,
            'Online Time': event.online_time ? DateUtils.formatDateWithLocalZone(event.online_time) : AppLabels.notApplicable,
            'Outage Duration (Minutes)': event.outage_Duration_In_Minutes !== null ? 
              formatOutageDuration(event.outage_Duration_In_Minutes?.toString() || '0', FORMAT_TYPES.VERBOSE) : 
              AppLabels.notApplicable,
            'Status': event.status || AppLabels.notApplicable
          });
        });
      }
    });

    return exportData;
  };


  if (loading || error || devicesLoading) {
    return null; // Don't render table if loading or error
  }

  return (
    <OutagePanel>
      <OutagePanelTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
           {contextText || `${AppLabels.outageLabels.outageDetailsTitle} (${rows.length} ${AppLabels.outageLabels.devicesLabel})`}
        
          {isDownloadButtonEnabled && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportClick}
              disabled={isExporting || rows.length === 0}
              size="small"
              sx={{
                backgroundColor: '#1976d2',
                color: 'white',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#1565c0',
                },
                '&:disabled': {
                  backgroundColor: '#cccccc',
                },
                minWidth: '120px'
              }}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          )}
        </Box>
      </OutagePanelTitle>
      <OutageTableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {TABLE_COLUMNS.map(column => (
                <TableCell key={column.key}>{column.header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.map(r => (
              <TableRow key={r.plugId}>
                {TABLE_COLUMNS.map(column => (
                  <TableCell key={column.key}>
                    {column.key === 'offlineEvents' 
                      ? column.render(r, handleOfflineEventsClick)
                      : column.render(r)
                    }
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={TABLE_COLUMNS.length}>
                  <OutageNoResults>{AppLabels.outageLabels.noDevicesFound || 'No devices found for current filters.'}</OutageNoResults>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </OutageTableContainer>

      {/* Pagination Component */}
      {rows.length > PAGINATION_CONFIG.DEFAULT_ROWS_PER_PAGE  && (
        <OutagePaginationContainer>
          <OutageTablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGINATION_CONFIG.ROWS_PER_PAGE_OPTIONS}
            showFirstButton
            showLastButton
          />
        </OutagePaginationContainer>
      )}

      {/* Offline Events Drawer */}
      <OfflineEventsDrawer
        open={drawerOpen}
        onClose={handleDrawerClose}
        plugId={selectedDevice?.plugId}
        deviceDetails={selectedDevice}
        startDate={startDate}
        endDate={endDate}
      />

      {/* Error Toast */}
      <Snackbar
        open={errorToast.open}
        autoHideDuration={6000}
        onClose={handleErrorToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleErrorToastClose} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {errorToast.message}
        </Alert>
      </Snackbar>
    </OutagePanel>
  );
};

// PropTypes validation for better code quality and SonarQube compliance
OutageTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    plugId: PropTypes.string,
    site: PropTypes.string,
    location: PropTypes.string,
    deviceMac: PropTypes.string,
    model: PropTypes.string,
    isModem: PropTypes.bool,
    modemId: PropTypes.string,
    statusNow: PropTypes.string,
    outageMinutes: PropTypes.number,
    offlineEvents: PropTypes.number,
    criticalDeviceList: PropTypes.string,
    criticalDevices: PropTypes.array,
    dualSupplyDevices: PropTypes.array
  })).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  devicesLoading: PropTypes.bool,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  contextText: PropTypes.string,
  isDownloadButtonEnabled: PropTypes.bool,
  searchParams: PropTypes.object,
};

// Default props to handle optional parameters
OutageTable.defaultProps = {
  loading: false,
  error: null,
  devicesLoading: false,
  startDate: '',
  endDate: '',
  contextText: null,
  isDownloadButtonEnabled: false,
  searchParams: null
};

export default OutageTable;
